<template>
  <!-- Casa do trecho palpitável é botão: dá para escolher onde a próxima letra cai, e o
       leitor de tela anuncia a posição. A casa que a regra revelou não é escolhível, e aí
       não vira botão nenhum — seria um alvo que não faz nada. -->
  <component
    :is="selectable ? 'button' : 'div'"
    class="box"
    :class="[`is-${state}`, { 'with-dock': !!dock, selectable, 'is-selected': selected }]"
    :style="delay"
    :type="selectable ? 'button' : null"
    :aria-label="selectable ? label : null"
    :aria-current="selectable && state === 'cursor' ? 'true' : null"
    @click="selectable ? $emit('select', index) : null"
  >
    <span class="glyph">{{ char }}</span>

    <!-- Dock acoplado: a banda na base da própria caixa, com a letra daquele palpite e a
         cor do que ela disse. Ele nasce com altura zero — antes do primeiro palpite a
         caixa é a de sempre. O canal é separado de propósito: result do palpite mora na
         banda, letra revelada pela regra mora no glifo (âmbar sublinhado). -->
    <span class="dock" :class="dock ? `res-${dock.result}` : ''">{{ dock ? dock.letter : '' }}</span>
  </component>
</template>

<script>
export default {
  name: 'GameTile',
  props: {
    char: { type: String, default: '' },
    state: { type: String, default: 'empty' },
    index: { type: Number, default: 0 },
    /** `{ letter, result }` do último palpite nesta casa, ou `null`. */
    dock: { type: Object, default: null },
    /** Esta casa aceita ser escolhida? Falso na revelada pela regra e na partida fechada. */
    selectable: { type: Boolean, default: false },
    /** É nela que a próxima letra cai — inclusive quando já tem letra dentro. */
    selected: { type: Boolean, default: false },
  },
  emits: ['select'],
  computed: {
    label() {
      const posicao = `Casa ${this.index + 1}`;
      return this.char ? `${posicao}, letra ${this.char}` : `${posicao}, vazia`;
    },
    delay() {
      // Escalonar por índice é o que transforma cinco animações simultâneas numa onda
      // (diretiva 06). 55ms é o intervalo do protótipo.
      return this.state === 'given' ? { animationDelay: `${this.index * 55}ms` } : null;
    },
  },
};
</script>

<style scoped>
/* A caixa cresce e encolhe com a palavra: `flex:1 1 0` com teto de 56px faz palavra curta
   não esticar e palavra longa caber sem estourar a linha.

   A **altura** não acompanha a largura. Com `aspect-ratio: 1`, uma palavra de nove letras
   dava 34px de casa em 390px de tela, e a banda de 22px comia dois terços dela — a letra
   saía cortada. Agora o glifo tem altura própria e a banda tem a sua: palavra curta
   continua quadrada (o glifo cresce até o teto), palavra longa só afina. */
.box {
  flex: 1 1 0;
  /* O botão herda fonte e alinhamento do agente: sem isto a casa vira um botão de sistema
     no meio do tabuleiro. */
  padding: 0;
  appearance: none;
  text-align: center;
  min-width: 0;
  max-width: 56px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: var(--r-button);
  background: var(--veil-1);
  border: 1.5px solid var(--line-2);
  color: var(--text);
  font: 700 21px/1 var(--f-title);
  text-transform: uppercase;
  transition: border-color 0.16s, background 0.16s, color 0.16s;
}

/* 52px é a altura do glifo no desenho, e o `aspect-ratio` só entra como piso: em palavra
   curta a casa volta a ser o quadrado de sempre. */
.glyph {
  display: grid;
  place-items: center;
  width: 100%;
  /* Altura = a maior entre a largura (o quadrado de sempre) e 52px, o piso do desenho.
     `flex: 1` aqui zerava a base e a altura voltava a seguir a largura. */
  aspect-ratio: 1;
  min-height: 52px;
}

/* A letra revelada pela regra continua marcada no glifo, e o sublinhado pontilhado a
   separa do âmbar do dock, que quer dizer outra coisa. */
.box.is-given .glyph { text-decoration: underline dotted 2px; text-underline-offset: 3px; }

.dock {
  height: 0;
  width: 100%;
  display: grid;
  place-items: center;
  font: 700 12px/1 var(--f-title);
  letter-spacing: 0.04em;
  color: transparent;
  transition: height 0.16s ease;
}

.box.with-dock .dock { height: 22px; }
.dock.res-right { background: var(--ok); color: var(--bg); }
.dock.res-moved { background: var(--amber); color: var(--bg); }
.dock.res-out { background: var(--veil-3); color: var(--text-2); }

/* Casa que a regra deu: a banda repete a letra já conhecida, em âmbar apagado — ela não foi
   avaliada, e não pode parecer resultado de palpite. Vem marcada assim do servidor. */
.dock.res-given { background: var(--amber-veil); color: var(--amber); }

/* Âmbar é a letra que o jogador pagou para ver — nunca "quase certo" (diretiva 02). */
.box.is-given {
  background: var(--amber-veil);
  border-color: var(--amber-line);
  color: var(--amber);
  animation: reveal 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes reveal {
  0% { transform: scale(0.72); opacity: 0; }
  62% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); }
}

.box.is-typed {
  border-color: var(--active-line);
  background: var(--active-veil);
  animation: pop 0.16s cubic-bezier(0.3, 1.6, 0.5, 1);
}

@keyframes pop {
  0% { transform: scale(0.86); }
  60% { transform: scale(1.08); }
  100% { transform: none; }
}

/* A caixa que pulsa é onde a próxima letra entra — vazia ou não. Antes só a vazia
   pulsava, e com o seletor de casa dava para escolher uma letra já digitada e não ver
   marca nenhuma: a tecla seguinte trocava uma letra sem aviso de qual. */
.box.is-cursor,
.box.is-typed.is-selected {
  border-color: var(--active);
  animation: cursor 1.3s ease-in-out infinite;
}

/* Na casa cheia a borda não basta: ela já tem contorno de digitada, e a diferença ficava
   num tom. O fundo mais forte é o que separa a escolhida das outras. */
.box.is-typed.is-selected { box-shadow: inset 0 0 0 2px var(--active); }

@keyframes cursor {
  0%, 100% { border-color: var(--active); }
  50% { border-color: var(--line-2); }
}


/* Escolher casa é toque, não arrasto: o cursor de mão diz que a casa responde. */
.box.selectable { cursor: pointer; }
.box.selectable:focus-visible { outline: 2px solid var(--active); outline-offset: 2px; }
</style>
